/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useEffect, useMemo } from "react";
import { groupVariantsByColor, getVariantColorHex } from "../utils/theme1Helpers";
import { formatNumber } from "utils/formatNumber";
import { getCardGalleryImages } from "../../../utils/productMainImages";
import MainImageCarousel from "../../components/MainImageCarousel";
import Swal from "sweetalert2";

export default function ProductModal({
  showProductModal,
  setShowProductModal,
  selectedProduct,
  products,
  theme,
  isMobile: isMobileProp,
  handleAddToCart
}) {
  const isMobile = isMobileProp ?? (typeof window !== "undefined" && window.innerWidth < 768);
  const [selectedColorHex, setSelectedColorHex] = useState(null);
  const [selectedSizeLabel, setSelectedSizeLabel] = useState(null);
  const [quantity, setQuantity] = useState(1);

  const product = useMemo(() => {
    return products.find(p => Number(p.id) === Number(selectedProduct?.productId));
  }, [selectedProduct, products]);

  const colorGroups = useMemo(() => groupVariantsByColor(product?.variants), [product]);
  const uniqueColors = useMemo(() => Object.values(colorGroups), [colorGroups]);
  const displayColors = useMemo(() => uniqueColors.filter((c) => c.hasColor), [uniqueColors]);

  useEffect(() => {
    if (showProductModal && product && uniqueColors.length > 0) {
      const initialVariant = product.variants.find(v => Number(v.variantId) === Number(selectedProduct?.variantId)) || product.variants[0];
      const initialHex = getVariantColorHex(initialVariant) || uniqueColors[0]?.hex;

      setSelectedColorHex(initialHex);

      const group = colorGroups[initialHex] || uniqueColors[0];
      const allSizes = [];
      group.variants.forEach(v => {
        const vSizes = v.options || v.sizes || [];
        vSizes.forEach(s => allSizes.push({ label: s.label || s.size, stock: Number(s.stock || s.stockQty || 0) }));
      });

      const firstAvailableSize = allSizes.find(opt => opt.stock > 0) || allSizes[0];
      if (firstAvailableSize) {
        setSelectedSizeLabel(firstAvailableSize.label);
      }
      setQuantity(1);
    }
  }, [showProductModal, product, selectedProduct, uniqueColors, colorGroups]);

  const activeColorGroup = colorGroups[selectedColorHex] || uniqueColors[0];

  const availableSizes = useMemo(() => {
    if (!activeColorGroup) return [];
    const sizeMap = new Map();
    activeColorGroup.variants.forEach(v => {
      const vSizes = v.options || v.sizes || [];
      vSizes.forEach(s => {
        const label = s.label || s.size;
        let stock = Number(s.stock || s.stockQty || 0);
        if (stock <= 0 && Number(v.stockQty || 0) > 0) stock = Number(v.stockQty || 0);
        if (!sizeMap.has(label)) {
          sizeMap.set(label, { label, stock, variant: v });
        } else {
          const existing = sizeMap.get(label);
          existing.stock += stock;
        }
      });
    });
    return Array.from(sizeMap.values());
  }, [activeColorGroup]);

  const activeSizeObj = useMemo(() => {
    return availableSizes.find(s => s.label === selectedSizeLabel) || availableSizes[0];
  }, [availableSizes, selectedSizeLabel]);

  const activeVariant = activeSizeObj?.variant || activeColorGroup?.variants?.[0];
  const galleryImages = useMemo(
    () => getCardGalleryImages(product, activeVariant?.imageUrl),
    [product, activeVariant]
  );

  const priceDetails = useMemo(() => {
    if (!activeVariant || !product) return { base: 0, discount: 0, final: 0 };

    const basePrice = Number(activeVariant.price || 0);
    const preCalculatedDiscount = Number(activeVariant.discount || 0);
    const preCalculatedFinal = Number(activeVariant.finalPrice || (basePrice - preCalculatedDiscount));

    const dValue = Number(product.discountValue || 0);
    const dType = Number(product.discountType || 0);

    let discountAmount = preCalculatedDiscount;
    let finalPrice = preCalculatedFinal;

    if (discountAmount === 0 && dValue > 0) {
      if (dType === 1) {
        discountAmount = (basePrice * dValue) / 100;
      } else {
        discountAmount = dValue;
      }
      finalPrice = basePrice - discountAmount;
    }

    return {
      base: basePrice,
      discount: discountAmount,
      final: Math.max(0, finalPrice)
    };
  }, [activeVariant, product]);

  if (!showProductModal || !product) return null;

  const handleColorChange = (hex) => {
    setSelectedColorHex(hex);
    const group = colorGroups[hex];
    const allSizes = [];
    group?.variants.forEach(v => {
      const vSizes = v.options || v.sizes || [];
      vSizes.forEach(s => allSizes.push({ label: s.label || s.size, stock: Number(s.stock || s.stockQty || 0) }));
    });
    const firstAvailable = allSizes.find(o => o.stock > 0) || allSizes[0];
    if (firstAvailable) {
      setSelectedSizeLabel(firstAvailable.label);
    } else {
      setSelectedSizeLabel(null);
    }
    setQuantity(1);
  };

  const isOutOfStock = !activeSizeObj || activeSizeObj.stock <= 0;
  const showOutOfStockAlert = () => {
    Swal.fire({
      icon: "info",
      title: "انتهى من المخزون",
      text: "هذا المنتج أو المقاس غير متوفر حالياً"
    });
  };

  const addToCart = () => {
    if (isOutOfStock) {
      showOutOfStockAlert();
      return;
    }
    handleAddToCart({
      tenantId: Number(product.tenantId),
      variantId: Number(activeVariant?.variantId),
      price: Number(priceDetails.final),
      color: getVariantColorHex(activeVariant) || "",
      colorName: getVariantColorHex(activeVariant) || "",
      size: String(selectedSizeLabel || ""),
      image: activeVariant?.imageUrl || product.mainImage,
      name: product.name,
      quantity: quantity,
      discount: priceDetails.discount,
      stockQty: Number(activeSizeObj?.stock || 0)
    }, quantity);
    setShowProductModal(false);
  };

  const qtyControl = (
    <div style={{
      display: "flex", alignItems: "center", background: "#F1F5F9",
      borderRadius: "30px", padding: isMobile ? "4px" : "6px", gap: isMobile ? "10px" : "15px",
      border: "1px solid #E2E8F0", flexShrink: 0
    }}>
      <button
        onClick={() => {
          if (activeSizeObj && quantity < activeSizeObj.stock) {
            setQuantity(q => q + 1);
          }
        }}
        style={{
          width: isMobile ? "38px" : "48px", height: isMobile ? "38px" : "48px", borderRadius: "50%", border: "none",
          background: "white", color: "#334155", cursor: "pointer", fontSize: isMobile ? "20px" : "24px",
          fontWeight: "600", boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
          display: "flex", alignItems: "center", justifyContent: "center"
        }}
        className="qty-btn"
      >+</button>
      <span style={{ fontSize: isMobile ? "16px" : "22px", fontWeight: "800", minWidth: "24px", textAlign: "center", color: "#1E293B" }}>{quantity}</span>
      <button
        onClick={() => setQuantity(q => Math.max(1, q - 1))}
        style={{
          width: isMobile ? "38px" : "48px", height: isMobile ? "38px" : "48px", borderRadius: "50%", border: "none",
          background: "white", color: "#334155", cursor: "pointer", fontSize: isMobile ? "20px" : "24px",
          fontWeight: "600", boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
          display: "flex", alignItems: "center", justifyContent: "center"
        }}
        className="qty-btn"
      >-</button>
    </div>
  );

  const addButton = (
    <button
      disabled={isOutOfStock}
      onClick={addToCart}
      style={{
        width: "100%", padding: isMobile ? "14px 16px" : "18px 24px", borderRadius: isMobile ? "16px" : "24px",
        background: isOutOfStock ? "#F3F4F6" : theme.primary,
        color: isOutOfStock ? "#9CA3AF" : "white",
        border: "none",
        fontSize: isMobile ? "15px" : "20px",
        fontWeight: "800",
        cursor: isOutOfStock ? "not-allowed" : "pointer",
        boxShadow: isOutOfStock ? "none" : `0 15px 30px ${theme.primary}22`,
        transition: "all 0.3s ease",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "10px"
      }}
      className="add-to-cart-btn"
    >
      <svg width={isMobile ? "18" : "24"} height={isMobile ? "18" : "24"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="9" cy="21" r="1"></circle>
        <circle cx="20" cy="21" r="1"></circle>
        <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
      </svg>
      {isOutOfStock ? "نفذت الكمية" : "إضافة للسلة"}
    </button>
  );

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.6)",
        zIndex: 10000,
        display: "flex",
        alignItems: isMobile ? "flex-end" : "stretch",
        justifyContent: "center",
        backdropFilter: "blur(10px)",
        padding: 0
      }}
      onClick={() => setShowProductModal(false)}
    >
      <div
        className="product-modal-container"
        style={{
          background: "white",
          borderRadius: isMobile ? "22px 22px 0 0" : 0,
          width: "100vw",
          maxWidth: "100vw",
          height: isMobile ? "min(92vh, 100%)" : "100vh",
          maxHeight: isMobile ? "92vh" : "100vh",
          overflow: "hidden",
          position: "relative",
          display: "flex",
          flexDirection: isMobile ? "column" : "row-reverse",
          boxShadow: "0 50px 100px rgba(0,0,0,0.25)",
          animation: "modalSlideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => setShowProductModal(false)}
          style={{
            position: "absolute", top: isMobile ? "10px" : "25px", left: isMobile ? "10px" : "25px",
            width: isMobile ? "38px" : "45px", height: isMobile ? "38px" : "45px", borderRadius: "50%", border: "none",
            background: "rgba(255,255,255,0.95)", color: "#333",
            cursor: "pointer", zIndex: 10001, fontSize: isMobile ? "18px" : "20px",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 10px 20px rgba(0,0,0,0.08)",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
          }}
          className="modal-close-btn"
        >✕</button>

        <div style={{
          flex: isMobile ? "0 0 auto" : 1.1,
          background: "#F7F9FC",
          padding: isMobile ? "36px 16px 12px" : "80px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderLeft: isMobile ? "none" : "1px solid #EEF1F5",
          position: "relative"
        }}>
          <div style={{ position: "relative", width: "100%", textAlign: "center" }}>
            {priceDetails.discount > 0 && (
              <div style={{
                position: "absolute", top: isMobile ? "0" : "-40px", right: "0",
                background: "#FF4757", color: "white", padding: isMobile ? "6px 12px" : "10px 20px",
                borderRadius: "16px", fontWeight: "900", fontSize: isMobile ? "12px" : "16px",
                boxShadow: "0 15px 30px rgba(255, 71, 87, 0.35)",
                zIndex: 2
              }}>
                {product.discountType === 1 ? `-${product.discountValue}%` : `OFF ${formatNumber(product.discountValue)}`}
              </div>
            )}
            <MainImageCarousel
              images={galleryImages}
              alt={product.name}
              variant="light"
              compact={isMobile}
              imgClassName="product-modal-image"
              imgStyle={{
                width: "100%",
                maxHeight: isMobile ? "210px" : "550px",
                objectFit: "contain",
                filter: "drop-shadow(0 30px 60px rgba(0,0,0,0.12))",
                transition: "transform 0.5s ease"
              }}
            />
          </div>
        </div>

        <div style={{
          flex: 1,
          minHeight: 0,
          padding: isMobile ? "8px 16px 16px" : "60px",
          textAlign: "right",
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          WebkitOverflowScrolling: "touch"
        }}>
          <header style={{ marginBottom: isMobile ? "12px" : "32px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "8px", marginBottom: isMobile ? "8px" : "16px", flexWrap: "wrap" }}>
              <span style={{
                background: theme.primary + "1A", color: theme.primary,
                padding: isMobile ? "4px 10px" : "6px 16px", borderRadius: "10px", fontSize: isMobile ? "11px" : "13px",
                fontWeight: "800", textTransform: "uppercase"
              }}>
                # {product.itemCode || product.id}
              </span>
              {(product.category || product.subCategory) && (
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  {product.subCategory && (
                    <span style={{ color: "#888", fontSize: isMobile ? "12px" : "14px", fontWeight: "600" }}>
                      {product.subCategory}
                    </span>
                  )}
                  {product.category && product.subCategory && <span style={{ color: "#DDD" }}>|</span>}
                  {product.category && (
                    <span style={{ color: theme.primary, fontSize: isMobile ? "12px" : "14px", fontWeight: "700" }}>
                      {product.category}
                    </span>
                  )}
                </div>
              )}
            </div>
            <h2 style={{
              fontSize: isMobile ? "20px" : "48px",
              fontWeight: "950",
              margin: isMobile ? "0 0 8px 0" : "0 0 16px 0",
              color: "#1E1E1E",
              lineHeight: "1.25"
            }}>{product.name}</h2>

            {product.tags && product.tags.length > 0 && (
              <div style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "8px",
                marginBottom: isMobile ? "10px" : "20px",
                justifyContent: "flex-end"
              }}>
                {product.tags.map((tag, idx) => (
                  <span
                    key={idx}
                    style={{
                      fontSize: isMobile ? "11px" : "13px",
                      fontWeight: "800",
                      padding: isMobile ? "4px 10px" : "6px 15px",
                      borderRadius: "10px",
                      background: theme.primary + "15",
                      color: theme.primary,
                    }}
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            )}

            {product.description && (
              <p style={{ color: "#777", fontSize: isMobile ? "13px" : "18px", lineHeight: "1.7", fontWeight: "400", margin: 0 }}>{product.description}</p>
            )}
          </header>

          <div style={{
            background: "linear-gradient(to left, #FAFAFA, #FFFFFF)",
            padding: isMobile ? "12px 14px" : "30px",
            borderRadius: isMobile ? "16px" : "32px",
            border: "1px solid #F0F0F0",
            marginBottom: isMobile ? "14px" : "40px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}>
            {priceDetails.discount > 0 ? (
              <div style={{ textAlign: "right", paddingRight: isMobile ? "12px" : "24px", borderRight: "2px solid #EEE" }}>
                <span style={{ fontSize: isMobile ? "11px" : "14px", color: "#AAA", display: "block", marginBottom: "4px", fontWeight: "700" }}>السعر الأصلي</span>
                <span style={{ textDecoration: "line-through", color: "#CCC", fontSize: isMobile ? "14px" : "24px", fontWeight: "700" }}>
                  {formatNumber(priceDetails.base)}
                </span>
              </div>
            ) : <div></div>}

            <div style={{ textAlign: "right" }}>
              <span style={{ fontSize: isMobile ? "11px" : "14px", color: "#AAA", display: "block", marginBottom: "4px", fontWeight: "700" }}>
                {priceDetails.discount > 0 ? "السعر بعد الخصم" : "السعر"}
              </span>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "flex-end", gap: "6px" }}>
                <span style={{ fontSize: isMobile ? "26px" : "48px", fontWeight: "950", color: theme.primary, letterSpacing: "-1px" }}>{formatNumber(priceDetails.final)}</span>
                <span style={{ fontSize: isMobile ? "13px" : "20px", fontWeight: "800", color: theme.primary }}>IQD</span>
              </div>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: isMobile ? "16px" : "32px", marginBottom: isMobile ? "12px" : "48px" }}>
            {displayColors.length > 0 && (
              <div>
                <h4 style={{ marginBottom: isMobile ? "10px" : "16px", fontWeight: "900", fontSize: isMobile ? "14px" : "17px", color: "#222", display: "flex", alignItems: "center", gap: "8px", justifyContent: "flex-end" }}>
                  <span style={{ color: "#666", fontWeight: "600", fontSize: isMobile ? "12px" : "14px" }}>({activeColorGroup?.name})</span>
                  الألوان المختارة
                </h4>
                <div style={{ display: "flex", gap: isMobile ? "10px" : "14px", flexWrap: "wrap", flexDirection: "row-reverse" }}>
                  {displayColors.map((c) => {
                    const isThisSelected = selectedColorHex === c.hex;
                    return (
                      <button
                        key={c.hex}
                        onClick={() => handleColorChange(c.hex)}
                        style={{
                          width: isMobile ? "36px" : "52px", height: isMobile ? "36px" : "52px", borderRadius: isMobile ? "12px" : "18px",
                          background: c.hex,
                          border: isThisSelected ? `2.5px solid white` : "1px solid rgba(0,0,0,0.08)",
                          outline: isThisSelected ? `3px solid ${theme.primary}` : "none",
                          cursor: "pointer",
                          transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                          transform: isThisSelected ? "scale(1.1)" : "scale(1)",
                          boxShadow: isThisSelected ? `0 12px 24px ${theme.primary}33` : "none"
                        }}
                        title={c.name}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {availableSizes.length > 0 && (
              <div>
                <h4 style={{ marginBottom: isMobile ? "10px" : "16px", fontWeight: "900", fontSize: isMobile ? "14px" : "17px", color: "#222", textAlign: "right" }}>المقاس المتاح:</h4>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", flexDirection: "row-reverse" }}>
                  {availableSizes.map((opt, idx) => {
                    const isActive = selectedSizeLabel === opt.label;
                    return (
                      <button
                        key={idx}
                        onClick={() => {
                          if (opt.stock <= 0) {
                            showOutOfStockAlert();
                            return;
                          }
                          setSelectedSizeLabel(opt.label);
                        }}
                        style={{
                          minWidth: isMobile ? "64px" : "90px",
                          padding: isMobile ? "8px 12px" : "16px 20px",
                          borderRadius: isMobile ? "12px" : "20px",
                          fontWeight: "800",
                          fontSize: isMobile ? "13px" : "15px",
                          border: isActive ? `2px solid ${theme.primary}` : "1.5px solid #F0F2F5",
                          background: isActive ? theme.primary : "#FFFFFF",
                          color: isActive ? "white" : (opt.stock <= 0 ? "#D1D5DB" : "#333"),
                          cursor: opt.stock <= 0 ? "not-allowed" : "pointer",
                          transition: "all 0.3s ease",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: "2px",
                          boxShadow: isActive ? `0 10px 20px ${theme.primary}22` : "none"
                        }}
                      >
                        {opt.label}
                        <span style={{ fontSize: "10px", opacity: 0.6 }}>{opt.stock} قطعة</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {!isMobile && (
            <div style={{ marginTop: "auto", display: "flex", gap: "20px", flexDirection: "row-reverse", alignItems: "center" }}>
              <div style={{ flex: 1.5, width: "100%" }}>{addButton}</div>
              {qtyControl}
            </div>
          )}
        </div>

        {isMobile && (
          <div style={{
            display: "flex",
            gap: "10px",
            alignItems: "center",
            padding: "10px 16px calc(12px + env(safe-area-inset-bottom))",
            borderTop: "1px solid #F0F0F0",
            background: "white",
            boxShadow: "0 -8px 24px rgba(0,0,0,0.06)"
          }}>
            {!isOutOfStock && qtyControl}
            <div style={{ flex: 1 }}>{addButton}</div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes modalSlideUp {
          from { opacity: 0; transform: translateY(40px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }

        .product-modal-container::-webkit-scrollbar {
          display: none;
        }

        .modal-close-btn:hover {
          transform: rotate(90deg) scale(1.1);
          background: #333 !important;
          color: white !important;
        }

        .product-modal-image:hover {
          transform: scale(1.05);
        }

        .add-to-cart-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          filter: brightness(1.05);
          box-shadow: 0 20px 40px ${theme.primary}33;
        }

        .qty-btn:active {
          transform: scale(0.9);
        }

        .qty-btn:hover {
          background: ${theme.primary} !important;
          color: white !important;
        }
      `}</style>
    </div>
  );
}
